/**
 * 设备部署可视化管理系统 v2
 *
 * 数据模型：
 * - 设备属于场馆 (room_id IS NULL 表示未分配到房间)
 * - 可分配设备列表：场馆下所有 room_id IS NULL 的设备
 *
 * 布局：
 * - 顶部: 商家选择 + 场馆选择 + 添加设备按钮
 * - 左侧: 房间列表 + 可分配设备列表 + 操作说明
 * - 中间: 房间平面图（含待定位设备区域，支持缩放和平移）
 *
 * 操作流程：
 * 1. 选择商家和场馆
 * 2. 点击"添加设备"创建设备到场馆（room_id IS NULL）
 * 3. 从左侧选择房间查看平面图
 * 4. 拖拽"可分配设备"到房间网格自动分配并定位
 * 5. 拖拽房间内"待定位设备"精确定位到网格
 */
import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  Row,
  Col,
  Select,
  Card,
  List,
  Table,
  Typography,
  Space,
  Button,
  Upload,
  Modal,
  Form,
  Input,
  InputNumber,
  message,
  Spin,
  Badge,
  Tooltip,
  Checkbox,
  Divider,
  Tag,
  Popconfirm,
  Slider,
} from 'antd'
import {
  HomeOutlined,
  UploadOutlined,
  PlusOutlined,
  MinusOutlined,
  ReloadOutlined,
  DeleteOutlined,
  EditOutlined,
  CheckSquareOutlined,
  CarryOutOutlined,
  ArrowDownOutlined,
  TableOutlined,
  AppstoreOutlined,
  SaveOutlined,
  CloseOutlined,
} from '@ant-design/icons'
import { merchantRoomApi } from '@/services/api'
import { devicesApi } from '@/services/devices'
import type {
  Device,
  DeviceType,
  DeviceCategory,
  CreateDeviceRequest,
  RoomDevicesDetail,
  FloorplanPosition,
} from '@/types/devices'
import { DeviceLayer, LAYER_CONFIG } from '@/types/devices'

const { Title, Text } = Typography
const { Option } = Select
const { TextArea } = Input

// 设备图标映射
const DEVICE_ICONS: Record<string, string> = {
  light: '💡',
  speaker: '🔊',
  screen: '📺',
  sensor: '📡',
  camera: '📷',
  default: '📦',
}

// 设备类型选项
const DEVICE_TYPE_OPTIONS: { label: string; value: DeviceType }[] = [
  { label: '💡 灯光', value: 'light' },
  { label: '🔊 音响', value: 'speaker' },
  { label: '📺 屏幕', value: 'screen' },
  { label: '📡 传感器', value: 'sensor' },
  { label: '📷 摄像头', value: 'camera' },
]

// 设备类型 → 设备分类 映射（后端需要，但自动填充，用户无感知）
const DEVICE_TYPE_TO_CATEGORY: Record<DeviceType, string> = {
  light: 'lighting',
  speaker: 'audio',
  screen: 'display',
  sensor: 'sensor',
  camera: 'camera',
}

// 网格配置常量（移到组件外部，供所有组件使用）
const GRID_SIZE = 10  // 每格代表 10cm
const CELL_PIXEL_SIZE = 5  // 每格显示 5px（适合大网格）
const DEVICE_ICON_SIZE = 24  // 设备图标固定大小（像素）

interface Merchant {
  id: number
  company_name: string
}

interface Venue {
  id: number
  venue_name: string
}

interface Room {
  id: number
  room_name: string
  room_number?: string
  venue_id: number
}

// 比例尺组件（定义在主组件外部，避免闭包问题）
interface ScaleBarProps {
  position: 'top-left' | 'bottom-right'
  pixelLength: number
  label: string
  scalePercent: number
}

const ScaleBar: React.FC<ScaleBarProps> = ({ position, pixelLength, label, scalePercent }) => {
  const positionStyle: React.CSSProperties =
    position === 'top-left'
      ? { top: 16, left: 16 }
      : { bottom: 16, right: 16 }

  return (
    <div
      style={{
        position: 'absolute',
        ...positionStyle,
        background: 'rgba(255, 255, 255, 0.95)',
        border: '1px solid #d9d9d9',
        borderRadius: 4,
        padding: '8px 12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        pointerEvents: 'none', // 不阻挡拖拽
        zIndex: 1000,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {/* 比例尺线条 */}
        <div
          style={{
            height: 3,
            backgroundColor: '#333',
            position: 'relative',
            width: pixelLength,
          }}
        >
          {/* 左端点 */}
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: -3,
              width: 1,
              height: 9,
              backgroundColor: '#333',
            }}
          />
          {/* 右端点 */}
          <div
            style={{
              position: 'absolute',
              right: 0,
              top: -3,
              width: 1,
              height: 9,
              backgroundColor: '#333',
            }}
          />
        </div>
        {/* 标签 */}
        <span style={{ fontSize: 12, color: '#333', fontWeight: 500 }}>
          {label}
        </span>
        {/* 缩放比例 */}
        <span style={{ fontSize: 11, color: '#666' }}>({scalePercent}%)</span>
      </div>
    </div>
  )
}

// 滑动缩放控制组件（类似百度地图）
interface ZoomSliderProps {
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
  step?: number
  label?: string
  vertical?: boolean
  style?: React.CSSProperties
}

const ZoomSlider: React.FC<ZoomSliderProps> = ({
  value,
  onChange,
  min = 1,
  max = 100000,
  step = 1,
  label,
  vertical = true,
  style,
}) => {
  const percent = Math.round(value * 100)

  return (
    <div
      style={{
        background: 'rgba(255, 255, 255, 0.95)',
        border: '1px solid #d9d9d9',
        borderRadius: 4,
        padding: vertical ? '12px 8px' : '8px 12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        pointerEvents: 'none', // 不阻挡拖拽
        zIndex: 1000,
        ...style,
      }}
    >
      {label && (
        <div style={{ fontSize: 11, color: '#666', marginBottom: 8, textAlign: 'center' }}>
          {label}
        </div>
      )}
      <Slider
        vertical={vertical}
        min={min}
        max={max}
        step={step}
        value={percent}
        onChange={(val) => onChange(val / 100)}
        style={{ ...vertical ? { height: 100 } : { width: 120 }, pointerEvents: 'auto' }}
        tooltip={{ formatter: (val) => `${val}%` }}
      />
      <div style={{ fontSize: 12, fontWeight: 500, textAlign: 'center', marginTop: 8 }}>
        {percent}%
      </div>
    </div>
  )
}

// 组件 Props 类型定义
interface DraggableDeviceProps {
  device: Device
  showStatus?: boolean
  compact?: boolean
  isInRoom?: boolean
  selectable?: boolean
  isSelected: boolean
  toggleDeviceSelection: (deviceId: number) => void
  handleRemoveDeviceFromRoom: (deviceId: number) => void
}

// 可拖拽设备组件（移到主组件外部）
const DraggableDevice: React.FC<DraggableDeviceProps> = ({
  device,
  showStatus = false,
  compact = false,
  isInRoom = false,
  selectable = false,
  isSelected,
  toggleDeviceSelection,
  handleRemoveDeviceFromRoom,
}) => {
  const [isDragging, setIsDragging] = React.useState(false)

  const handleDragStart = (e: React.DragEvent) => {
    setIsDragging(true)
    e.dataTransfer.setData('text/plain', String(device.id))
    e.dataTransfer.effectAllowed = 'move'
    const dragImage = e.currentTarget.cloneNode(true) as HTMLElement
    dragImage.style.position = 'absolute'
    dragImage.style.top = '-1000px'
    e.dataTransfer.setDragImage(dragImage, 0, 0)
  }

  const handleDragEnd = () => {
    setIsDragging(false)
  }

  const pos = device.physical_position as any
  const gridX = pos?.floorplan?.grid_x
  const gridY = pos?.floorplan?.grid_y

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      style={{
        padding: compact ? '4px 8px' : '8px 12px',
        background: isSelected ? '#e6f7ff' : '#f0f0f0',
        borderRadius: 4,
        border: isSelected ? '2px solid #1890ff' : '1px solid #d9d9d9',
        cursor: 'grab',
        opacity: isDragging ? 0.5 : 1,
        touchAction: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: compact ? 4 : 8,
        position: 'relative',
      }}
    >
      {selectable && (
        <Checkbox
          checked={isSelected}
          onChange={() => toggleDeviceSelection(device.id)}
          onClick={(e) => e.stopPropagation()}
        />
      )}
      <span style={{ fontSize: compact ? 14 : 20 }}>{DEVICE_ICONS[device.device_type] || DEVICE_ICONS.default}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 500, fontSize: compact ? 12 : 14 }}>{device.device_name}</div>
        {!compact && (
          <div style={{ fontSize: 12, color: '#666' }}>
            {device.device_id}
            {typeof gridX === 'number' && typeof gridY === 'number' && (
              <span> | 位置: ({gridX}, {gridY})</span>
            )}
          </div>
        )}
      </div>
      {isInRoom && (
        <Tooltip title="移除到未分配">
          <Button
            type="text"
            size="small"
            icon={<MinusOutlined />}
            onClick={(e) => {
              e.stopPropagation()
              handleRemoveDeviceFromRoom(device.id)
            }}
            style={{ color: '#ff4d4f' }}
          />
        </Tooltip>
      )}
      {showStatus && (
        <Badge
          status={device.status === 'online' ? 'success' : 'default'}
          text={device.status === 'online' ? '在线' : '离线'}
        />
      )}
    </div>
  )
}

interface DraggableGridDeviceProps {
  device: Device
  x: number
  y: number
  cellSize: number
  scale: number
  savingLayout: boolean
}

// 网格中的可拖拽设备（移到主组件外部）
const DraggableGridDevice: React.FC<DraggableGridDeviceProps> = ({
  device,
  x,
  y,
  cellSize,
  scale,
  savingLayout,
}) => {
  const [isDraggingLocal, setIsDraggingLocal] = React.useState(false)

  const handleDragStart = (e: React.DragEvent) => {
    setIsDraggingLocal(true)
    e.dataTransfer.setData('text/plain', String(device.id))
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragEnd = () => {
    setIsDraggingLocal(false)
  }

  const getDeviceLayer = (d: Device): DeviceLayer | null => {
    if (d.extra_metadata?.layer) return d.extra_metadata.layer as DeviceLayer
    const pos = d.physical_position as any
    if (pos?.floorplan?.layer) return pos.floorplan.layer as DeviceLayer
    if (d.device_type === 'sensor') return DeviceLayer.FLOOR_L1
    return null
  }

  const deviceLayer = getDeviceLayer(device)
  const layerConfig = deviceLayer ? LAYER_CONFIG[deviceLayer] : null

  const leftPosition = x * cellSize + cellSize / 2
  const topPosition = y * cellSize + cellSize / 2

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      style={{
        position: 'absolute',
        left: leftPosition,
        top: topPosition,
        transform: `translate(-50%, -50%) scale(${1 / scale})`,
        transformOrigin: 'center center',
        cursor: savingLayout ? 'wait' : 'grab',
        userSelect: 'none',
        opacity: isDraggingLocal ? 0.5 : 1,
        touchAction: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: DEVICE_ICON_SIZE,
        height: DEVICE_ICON_SIZE,
        border: layerConfig ? `2px solid ${layerConfig.color}` : '2px solid #999',
        borderRadius: '50%',
        backgroundColor: '#fff',
        boxShadow: layerConfig ? `0 0 8px ${layerConfig.color}80` : '0 2px 4px rgba(0,0,0,0.2)',
      }}
    >
      <Tooltip title={
        deviceLayer
          ? `${device.device_name} (${device.device_id}) - ${LAYER_CONFIG[deviceLayer].name}`
          : `${device.device_name} (${device.device_id})`
      }>
        <span style={{ fontSize: DEVICE_ICON_SIZE * 0.7 }}>
          {DEVICE_ICONS[device.device_type] || DEVICE_ICONS.default}
        </span>
      </Tooltip>
    </div>
  )
}

interface GridCanvasProps {
  scale: number
  position: { x: number; y: number }
  gridDimensions: { width: number; height: number }
  floorplan: any
  roomUndeployedDevices: Device[]
  roomDeployedDevices: Device[]
  availableDevices: Device[]  // 添加可分配设备列表
  onDeviceDrop: (device: Device, gridX: number, gridY: number) => void
  isDragging: boolean
  CELL_PIXEL_SIZE: number
  children: React.ReactNode
  backgroundImage: { scale: number; position: { x: number; y: number } }
  isBatchMode: boolean
  selectedDeviceIds: Set<number>
  toggleDeviceSelection: (deviceId: number) => void
  toggleSelectAll: (devices: Device[]) => void
  handleRemoveDeviceFromRoom: (deviceId: number) => void
}

// 网格画布组件（移到主组件外部）
const GridCanvas: React.FC<GridCanvasProps> = ({
  scale,
  position,
  gridDimensions,
  floorplan,
  roomUndeployedDevices,
  roomDeployedDevices,
  availableDevices,
  onDeviceDrop,
  isDragging,
  CELL_PIXEL_SIZE,
  children,
  backgroundImage,
  isBatchMode,
  selectedDeviceIds,
  toggleDeviceSelection,
  toggleSelectAll,
  handleRemoveDeviceFromRoom,
}) => {
  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault()
    const deviceId = event.dataTransfer.getData('text/plain')
    if (!deviceId) return

    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
    const x = (event.clientX - rect.left) / scale - position.x
    const y = (event.clientY - rect.top) / scale - position.y
    const gridX = Math.floor(x / CELL_PIXEL_SIZE)
    const gridY = Math.floor(y / CELL_PIXEL_SIZE)

    // 从所有设备列表中查找（包括可分配设备）
    const device = [...roomUndeployedDevices, ...roomDeployedDevices, ...availableDevices].find(d => d.id === parseInt(deviceId))
    if (device && gridX >= 0 && gridX < gridDimensions.width && gridY >= 0 && gridY < gridDimensions.height) {
      onDeviceDrop(device, gridX, gridY)
    }
  }

  const gridBackground = `
    linear-gradient(to right, rgba(232, 232, 232, 0.3) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(232, 232, 232, 0.3) 1px, transparent 1px)
  `
  const backgroundSize = `${CELL_PIXEL_SIZE}px ${CELL_PIXEL_SIZE}px`

  return (
    <div
      onDrop={handleDrop}
      onDragOver={(e) => e.preventDefault()}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
        transformOrigin: '0 0',
        transition: isDragging ? 'none' : 'transform 0.1s ease-out',
        width: gridDimensions.width * CELL_PIXEL_SIZE,
        height: gridDimensions.height * CELL_PIXEL_SIZE,
        background: gridBackground,
        backgroundSize: backgroundSize,
        boxShadow: '0 0 0 1px rgba(0,0,0,0.05), 0 4px 20px rgba(0,0,0,0.1)',
      }}
    >
      {floorplan?.image_url && (
        <img
          src={floorplan.image_url}
          alt="平面图"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            // 使用 naturalWidth/naturalHeight 让图片按自然尺寸显示
            width: 'auto',
            height: 'auto',
            maxWidth: 'none',  // 允许超出容器
            maxHeight: 'none',
            // 背景图跟随画布缩放，但有自己的独立缩放系数
            transform: `translate(${backgroundImage.position.x}px, ${backgroundImage.position.y}px) scale(${backgroundImage.scale})`,
            transformOrigin: '0 0',
            pointerEvents: 'none',
            opacity: 0.8,
            zIndex: 0,
            display: 'block',
          }}
        />
      )}
      {roomUndeployedDevices.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid #d9d9d9',
            borderRadius: 8,
            padding: 12,
            maxWidth: 300,
            width: '100%',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 8, color: '#666', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>待定位设备 ({roomUndeployedDevices.length})</span>
            {isBatchMode && (
              <Button size="small" onClick={() => toggleSelectAll(roomUndeployedDevices)}>
                {selectedDeviceIds.size === roomUndeployedDevices.length ? '取消全选' : '全选'}
              </Button>
            )}
          </div>
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            {roomUndeployedDevices.map((device) => (
              <DraggableDevice
                key={device.id}
                device={device}
                compact
                isInRoom={true}
                selectable={isBatchMode}
                isSelected={selectedDeviceIds.has(device.id)}
                toggleDeviceSelection={toggleDeviceSelection}
                handleRemoveDeviceFromRoom={handleRemoveDeviceFromRoom}
              />
            ))}
          </Space>
        </div>
      )}
      {children}
    </div>
  )
}

// 辅助函数：计算网格尺寸（移到主组件外部）
const getGridDimensions = (floorplan: any) => {
  if (floorplan?.grid) {
    return {
      width: floorplan.grid.width || 10000,
      height: floorplan.grid.height || 10000,
    }
  }
  return { width: 10000, height: 10000 }
}

// 辅助函数：获取设备层级（移到主组件外部）
const getDeviceLayer = (device: Device): DeviceLayer | null => {
  if (device.extra_metadata?.layer) {
    return device.extra_metadata.layer as DeviceLayer
  }
  const pos = device.physical_position as any
  if (pos?.floorplan?.layer) {
    return pos.floorplan.layer as DeviceLayer
  }
  if (device.device_type === 'sensor') {
    return DeviceLayer.FLOOR_L1
  }
  return null
}

// 辅助函数：计算比例尺数据（移到主组件外部）
const calculateScaleBarData = (
  scale: number,
  gridSize: number,
  cellPixelSize: number
) => {
  const cmPerGrid = gridSize
  const cmPerPixel = cmPerGrid / cellPixelSize
  const cmPerViewportPixel = cmPerPixel / scale

  const targetPixelLength = 150
  const targetCmLength = targetPixelLength * cmPerViewportPixel

  const niceScales = [
    { cm: 10, label: '10cm' },
    { cm: 50, label: '50cm' },
    { cm: 100, label: '1m' },
    { cm: 500, label: '5m' },
    { cm: 1000, label: '10m' },
    { cm: 5000, label: '50m' },
    { cm: 10000, label: '100m' },
    { cm: 50000, label: '500m' },
    { cm: 100000, label: '1km' },
  ]

  let bestScale = niceScales[0]
  let minDiff = Math.abs(targetCmLength - bestScale.cm)

  for (const scaleOption of niceScales) {
    const diff = Math.abs(targetCmLength - scaleOption.cm)
    if (diff < minDiff) {
      minDiff = diff
      bestScale = scaleOption
    }
  }

  const pixelLength = bestScale.cm / cmPerViewportPixel

  return {
    pixelLength,
    label: bestScale.label,
    scalePercent: Math.round(scale * 100),
  }
}

const DeviceDeployment: React.FC = () => {
  // 顶部选择器状态
  const [selectedMerchantId, setSelectedMerchantId] = useState<number | null>(null)
  const [selectedVenueId, setSelectedVenueId] = useState<number | null>(null)
  const [selectedRoomId, setSelectedRoomId] = useState<number | null>(null)

  // 数据状态
  const [merchants, setMerchants] = useState<Merchant[]>([])
  const [venues, setVenues] = useState<Venue[]>([])
  const [rooms, setRooms] = useState<Room[]>([])
  const [roomDevices, setRoomDevices] = useState<Device[]>([])
  const [availableDevices, setAvailableDevices] = useState<Device[]>([])  // 场馆可分配设备
  const [floorplan, setFloorplan] = useState<any>(null)

  // 层级选择状态（支持多选，叠加显示）
  const [selectedLayers, setSelectedLayers] = useState<Set<DeviceLayer>>(
    new Set([DeviceLayer.FLOOR_L1, DeviceLayer.CEILING_L1])  // 默认显示一楼
  )

  // 加载状态
  const [loading, setLoading] = useState(false)
  const [merchantsLoading, setMerchantsLoading] = useState(false)
  const [venuesLoading, setVenuesLoading] = useState(false)
  const [roomsLoading, setRoomsLoading] = useState(false)
  const [uploadingFloorplan, setUploadingFloorplan] = useState(false)
  const [savingLayout, setSavingLayout] = useState(false)

  // 画布缩放和平移状态（整个视图）
  const [scale, setScale] = useState(1)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })

  // 背景图独立状态（用于对齐网格）
  const [backgroundImage, setBackgroundImage] = useState({
    scale: 1,        // 背景图缩放比例
    position: { x: 0, y: 0 },  // 背景图偏移位置
  })
  const [isAdjustingBackground, setIsAdjustingBackground] = useState(false)  // 是否正在调整背景图
  // 背景图拖拽初始状态（记录鼠标视口坐标和背景图位置）
  const [backgroundDragStart, setBackgroundDragStart] = useState({
    mouseX: 0, mouseY: 0,  // 鼠标视口坐标
    bgX: 0, bgY: 0,         // 背景图位置（画布坐标系）
  })

  // 大型场馆：10000×10000 格 = 1000m×1000m = 100万平方米
  // 画布尺寸：10000 × 5px = 50000px × 50000px（通过平移和缩放查看）

  // 模态框状态
  const [addDeviceModalVisible, setAddDeviceModalVisible] = useState(false)
  const [editingDevice, setEditingDevice] = useState<Device | null>(null)
  const [form] = Form.useForm()

  // 批量选择状态
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<Set<number>>(new Set())
  const [isBatchMode, setIsBatchMode] = useState(false)

  // 视图模式：平面图视图 vs 设备配置表格视图
  const [viewMode, setViewMode] = useState<'floorplan' | 'table'>('floorplan')

  // 设备配置表格数据
  const [allDevices, setAllDevices] = useState<Device[]>([])
  const [devicesLoading, setDevicesLoading] = useState(false)
  const [editingCoordinate, setEditingCoordinate] = useState<{ id: number; field: string; value: any } | null>(null)

  // 表格过滤状态
  const [tableFilters, setTableFilters] = useState({
    deviceType: undefined as string | undefined,
    status: undefined as string | undefined,
    roomId: undefined as number | undefined,
  })
  const [tablePagination, setTablePagination] = useState({ current: 1, pageSize: 20 })

  // Canvas 容器引用（用于添加 passive: false 的事件监听器）
  const canvasContainerRef = useRef<HTMLDivElement>(null)

  // 获取商家列表
  const fetchMerchants = async () => {
    setMerchantsLoading(true)
    try {
      const response = await merchantRoomApi.getMerchantsForRooms()
      const merchantList = response.data?.items || []
      setMerchants(merchantList)
    } catch (error) {
      console.error('获取商户列表失败:', error)
      message.error('获取商户列表失败')
    } finally {
      setMerchantsLoading(false)
    }
  }

  // 获取场馆列表
  const fetchVenues = async (merchantId: number) => {
    if (!merchantId) {
      setVenues([])
      return
    }
    setVenuesLoading(true)
    try {
      const response = await merchantRoomApi.getVenuesByMerchant(merchantId)
      const venueList = response.data?.items || []
      setVenues(venueList)
    } catch (error) {
      console.error('获取场馆列表失败:', error)
      message.error('获取场馆列表失败')
      setVenues([])
    } finally {
      setVenuesLoading(false)
    }
  }

  // 获取房间列表
  const fetchRooms = async (venueId: number) => {
    if (!venueId) {
      setRooms([])
      return
    }
    setRoomsLoading(true)
    try {
      const response = await merchantRoomApi.getRoomsByVenue(venueId)
      const roomList = response.data?.items || []
      setRooms(roomList)
    } catch (error) {
      console.error('获取房间列表失败:', error)
      message.error('获取房间列表失败')
      setRooms([])
    } finally {
      setRoomsLoading(false)
    }
  }

  // 加载选中房间的设备
  const fetchRoomDevices = async (roomId: number) => {
    setLoading(true)
    try {
      const response = await devicesApi.getRoomDevicesDetail(roomId)
      setRoomDevices(response.devices || [])
      setFloorplan(response.floorplan || null)
    } catch (error) {
      console.error('加载房间设备失败:', error)
      message.error('加载房间设备失败')
      setRoomDevices([])
      setFloorplan(null)
    } finally {
      setLoading(false)
    }
  }

  // 加载场馆可分配设备列表
  const fetchVenueAvailableDevices = async (venueId: number) => {
    if (!venueId) {
      setAvailableDevices([])
      return
    }
    try {
      const response = await devicesApi.getVenueAvailableDevices(venueId)
      setAvailableDevices(response.devices || [])
    } catch (error) {
      console.error('加载可分配设备失败:', error)
      message.error('加载可分配设备失败')
      setAvailableDevices([])
    }
  }

  // 初始化
  useEffect(() => {
    fetchMerchants()
  }, [])

  // 商家改变时加载场馆，同时清理后续状态
  useEffect(() => {
    // 清理所有下游状态
    setSelectedVenueId(null)
    setSelectedRoomId(null)
    setRooms([])
    setRoomDevices([])
    setAvailableDevices([])
    setFloorplan(null)

    if (selectedMerchantId) {
      fetchVenues(selectedMerchantId)
    } else {
      setVenues([])
    }
  }, [selectedMerchantId])

  // 场馆改变时加载房间和可分配设备
  useEffect(() => {
    if (selectedVenueId) {
      fetchRooms(selectedVenueId)
      fetchVenueAvailableDevices(selectedVenueId)
    } else {
      setRooms([])
      setAvailableDevices([])
    }
  }, [selectedVenueId])

  // 房间改变时加载设备和恢复背景图配置
  useEffect(() => {
    if (selectedRoomId) {
      // 清空初始 floorplan 缓存，准备加载新房间的数据
      initialFloorplanRef.current = null
      // 加载设备并恢复背景图配置
      loadRoomWithBackgroundConfig(selectedRoomId)
    } else {
      setRoomDevices([])
      setFloorplan(null)
    }
  }, [selectedRoomId])

  // 加载房间数据并恢复背景图配置
  const loadRoomWithBackgroundConfig = async (roomId: number) => {
    setLoading(true)
    try {
      const response = await devicesApi.getRoomDevicesDetail(roomId)
      setRoomDevices(response.devices || [])
      setFloorplan(response.floorplan || null)

      // 恢复背景图配置（缩放和位置）
      if (response.floorplan) {
        const fp = response.floorplan
        setBackgroundImage({
          scale: fp.scale || 1,
          position: {
            x: fp.position_x || 0,
            y: fp.position_y || 0
          }
        })
      }
    } catch (error) {
      console.error('加载房间设备失败:', error)
      message.error('加载房间设备失败')
      setRoomDevices([])
      setFloorplan(null)
    } finally {
      setLoading(false)
    }
  }

  // ========== 批量操作功能 ==========

  // 切换设备选择状态
  const toggleDeviceSelection = (deviceId: number) => {
    setSelectedDeviceIds(prev => {
      const newSet = new Set(prev)
      if (newSet.has(deviceId)) {
        newSet.delete(deviceId)
      } else {
        newSet.add(deviceId)
      }
      return newSet
    })
  }

  // 全选/取消全选
  const toggleSelectAll = (devices: Device[]) => {
    if (selectedDeviceIds.size === devices.length) {
      // 全部选中则取消全选
      setSelectedDeviceIds(new Set())
    } else {
      // 全选
      setSelectedDeviceIds(new Set(devices.map(d => d.id)))
    }
  }

  // 从房间移除设备到未分配
  const handleRemoveDeviceFromRoom = async (deviceId: number) => {
    try {
      await devicesApi.removeDeviceFromRoom(deviceId)
      message.success('设备已移除到未分配')
      // 刷新数据
      if (selectedRoomId) {
        await fetchRoomDevices(selectedRoomId)
      }
      if (selectedVenueId) {
        await fetchVenueAvailableDevices(selectedVenueId)
      }
      // 清除选择状态
      setSelectedDeviceIds(new Set())
    } catch (error) {
      console.error('移除设备失败:', error)
      message.error('移除设备失败')
    }
  }

  // 批量移除设备到未分配
  const handleBatchRemove = async () => {
    if (selectedDeviceIds.size === 0) {
      message.warning('请先选择设备')
      return
    }

    try {
      const response = await devicesApi.batchUpdateDevices({
        device_ids: Array.from(selectedDeviceIds),
        room_id: 0  // 0 表示移除到未分配
      })
      message.success(`已移除 ${response.updated} 个设备到未分配`)
      // 刷新数据
      if (selectedRoomId) {
        await fetchRoomDevices(selectedRoomId)
      }
      if (selectedVenueId) {
        await fetchVenueAvailableDevices(selectedVenueId)
      }
      // 清除选择状态
      setSelectedDeviceIds(new Set())
    } catch (error) {
      console.error('批量移除失败:', error)
      message.error('批量移除失败')
    }
  }

  // 批量添加到房间
  const handleBatchAddToRoom = async (targetRoomId: number) => {
    if (selectedDeviceIds.size === 0) {
      message.warning('请先选择设备')
      return
    }

    try {
      const response = await devicesApi.batchUpdateDevices({
        device_ids: Array.from(selectedDeviceIds),
        room_id: targetRoomId
      })
      message.success(`已添加 ${response.updated} 个设备到房间`)
      // 刷新数据
      if (selectedRoomId) {
        await fetchRoomDevices(selectedRoomId)
      }
      if (selectedVenueId) {
        await fetchVenueAvailableDevices(selectedVenueId)
      }
      // 清除选择状态
      setSelectedDeviceIds(new Set())
    } catch (error) {
      console.error('批量添加失败:', error)
      message.error('批量添加失败')
    }
  }

  // ========== 自动适配视口功能 ==========

  // 自动适配视口：计算合适的缩放和位置，让整个网格居中显示
  const fitToViewport = () => {
    // 默认网格尺寸（如果没有平面图）
    const defaultGridWidth = 100
    const defaultGridHeight = 100
    const gridWidth = gridDimensions.width || defaultGridWidth
    const gridHeight = gridDimensions.height || defaultGridHeight

    // 计算画布的物理尺寸（像素）
    const canvasPixelWidth = gridWidth * CELL_PIXEL_SIZE
    const canvasPixelHeight = gridHeight * CELL_PIXEL_SIZE

    // 获取容器尺寸（画布显示区域）
    const container = canvasContainerRef.current
    if (!container) return

    const containerWidth = container.clientWidth || 800
    const containerHeight = container.clientHeight || 600

    // 留一些边距（20%）
    const padding = 0.8
    const availableWidth = containerWidth * padding
    const availableHeight = containerHeight * padding

    // 计算缩放比例（让画布适配到容器中）
    const scaleX = availableWidth / canvasPixelWidth
    const scaleY = availableHeight / canvasPixelHeight
    const newScale = Math.min(scaleX, scaleY, 1) // 最多1倍，不放大

    // 计算居中位置
    const scaledWidth = canvasPixelWidth * newScale
    const scaledHeight = canvasPixelHeight * newScale
    const newX = (containerWidth - scaledWidth) / 2
    const newY = (containerHeight - scaledHeight) / 2

    setScale(newScale)
    setPosition({ x: newX, y: newY })
    // 注意：不再重置 backgroundImage，由用户独立控制背景图缩放
  }

  // ========== 背景图配置保存功能 ==========

  // 防抖保存背景图配置
  const saveBackgroundConfigTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const initialFloorplanRef = useRef<any>(null)

  // 保存初始加载的 floorplan 数据（只保存一次，之后不再更新）
  useEffect(() => {
    if (floorplan && !initialFloorplanRef.current) {
      initialFloorplanRef.current = floorplan
    }
  }, [floorplan])

  const saveBackgroundConfig = useCallback((roomId: number | undefined, bgConfig: typeof backgroundImage) => {
    if (!roomId || !floorplan?.image_url) return

    // 清除之前的定时器
    if (saveBackgroundConfigTimeoutRef.current) {
      clearTimeout(saveBackgroundConfigTimeoutRef.current)
    }

    // 设置新的定时器（500ms 后保存）
    saveBackgroundConfigTimeoutRef.current = setTimeout(async () => {
      try {
        // 使用当前 floorplan state 的数据，覆盖 scale 和 position
        await merchantRoomApi.updateRoom(roomId, {
          equipment: {
            floorplan: {
              ...floorplan,
              scale: bgConfig.scale,
              position_x: bgConfig.position.x,
              position_y: bgConfig.position.y,
            }
          }
        })
        message.success('背景图配置已保存')
      } catch (error) {
        console.error('保存背景图配置失败:', error)
        message.error('保存背景图配置失败')
      }
    }, 500)
  }, [floorplan])

  // 当背景图配置变化时，自动保存
  useEffect(() => {
    saveBackgroundConfig(selectedRoomId, backgroundImage)
    return () => {
      if (saveBackgroundConfigTimeoutRef.current) {
        clearTimeout(saveBackgroundConfigTimeoutRef.current)
      }
    }
  }, [backgroundImage.scale, backgroundImage.position.x, backgroundImage.position.y, selectedRoomId, saveBackgroundConfig])



  // 当选择房间后，自动适配视口
  const hasCalledFitToViewportRef = useRef(false)

  useEffect(() => {
    if (selectedRoomId && !loading && !hasCalledFitToViewportRef.current) {
      // 延迟执行，确保DOM已更新
      setTimeout(() => {
        fitToViewport()
        hasCalledFitToViewportRef.current = true
      }, 100)
    }
  }, [selectedRoomId, loading])

  // 切换房间时重置标记
  useEffect(() => {
    hasCalledFitToViewportRef.current = false
  }, [selectedRoomId])

  // ========== 设备配置表格功能 ==========

  // 获取所有设备列表（用于表格视图）
  const fetchAllDevices = async () => {
    if (!selectedVenueId) {
      setAllDevices([])
      return
    }
    setDevicesLoading(true)
    try {
      const response = await devicesApi.getAllDevices({
        venueId: selectedVenueId,
        roomId: tableFilters.roomId,
        deviceType: tableFilters.deviceType,
        status: tableFilters.status,
      })
      setAllDevices(response.devices || [])
    } catch (error) {
      console.error('获取设备列表失败:', error)
      message.error('获取设备列表失败')
      setAllDevices([])
    } finally {
      setDevicesLoading(false)
    }
  }

  // 编辑设备坐标
  const handleCoordinateEdit = async (deviceId: number, field: string, value: any) => {
    try {
      const device = allDevices.find(d => d.id === deviceId)
      if (!device) return

      // 更新物理位置
      const currentPos = device.physical_position as any || {}
      const floorplanPos = currentPos.floorplan || {}

      if (field === 'gridX') {
        floorplanPos.grid_x = value
      } else if (field === 'gridY') {
        floorplanPos.grid_y = value
      }

      // 调用API更新
      await devicesApi.updateDeviceLayout(device.room_id!, deviceId, {
        is_deployed: true,
        floorplan: floorplanPos,
      })

      message.success('坐标更新成功')
      setEditingCoordinate(null)
      // 刷新数据
      await fetchAllDevices()
    } catch (error) {
      console.error('更新坐标失败:', error)
      message.error('更新坐标失败')
    }
  }

  // 编辑设备信息
  const handleDeviceInfoEdit = async (deviceId: number, field: string, value: any) => {
    try {
      await devicesApi.updateDevice(deviceId, { [field]: value })
      message.success('更新成功')
      // 刷新数据
      await fetchAllDevices()
    } catch (error) {
      console.error('更新设备信息失败:', error)
      message.error('更新失败')
    }
  }

  // 过滤条件变化时刷新数据
  useEffect(() => {
    if (viewMode === 'table') {
      fetchAllDevices()
    }
  }, [viewMode, selectedVenueId, tableFilters])

  // 添加 passive: false 的 wheel 事件监听器（修复 preventDefault 警告）
  useEffect(() => {
    const container = canvasContainerRef.current
    if (!container) return

    const handleWheelNative = (e: WheelEvent) => {
      e.preventDefault()

      // 获取容器的边界
      const rect = container.getBoundingClientRect()
      const mouseX = e.clientX - rect.left
      const mouseY = e.clientY - rect.top

      // 检测修饰键
      const isAltKey = e.altKey  // Alt 键：调整背景图
      const isShiftKey = e.shiftKey  // Shift 键：快速缩放

      if (isAltKey) {
        // Alt + 滚轮：缩放背景图
        const delta = e.deltaY > 0 ? -0.05 : 0.05
        const newBgScale = Math.max(0.01, Math.min(1000, backgroundImage.scale + delta))  // 最小 1%，最大 100000%

        // 以鼠标位置为中心缩放背景图
        const bgGridX = (mouseX - position.x - backgroundImage.position.x) / backgroundImage.scale
        const bgGridY = (mouseY - position.y - backgroundImage.position.y) / backgroundImage.scale

        const newBgX = mouseX - position.x - bgGridX * newBgScale
        const newBgY = mouseY - position.y - bgGridY * newBgScale

        setBackgroundImage({
          scale: newBgScale,
          position: { x: newBgX, y: newBgY }
        })
      } else {
        // 普通/Shift + 滚轮：缩放画布
        const speed = isShiftKey ? 2 : 1  // Shift 键：2倍速度
        const delta = e.deltaY > 0 ? -0.01 * speed : 0.01 * speed  // 每次滚动 1%（Shift 键时 2%）
        const newScale = Math.max(0.01, Math.min(1000, scale + delta))  // 最小 1%，最大 100000%

        // 计算鼠标在缩放前的网格坐标
        const gridX = (mouseX - position.x) / scale
        const gridY = (mouseY - position.y) / scale

        // 调整 position，使鼠标位置在缩放后保持不变
        const newX = mouseX - gridX * newScale
        const newY = mouseY - gridY * newScale

        setScale(newScale)
        setPosition({ x: newX, y: newY })
      }
    }

    container.addEventListener('wheel', handleWheelNative, { passive: false })

    return () => {
      container.removeEventListener('wheel', handleWheelNative)
    }
  }, [scale, position, backgroundImage])

  // 计算房间设备数量
  const getRoomDeviceCount = (roomId: number) => {
    return roomDevices.filter((d) => d.room_id === roomId).length
  }

  // 缩放控制（按钮）- 画布
  const handleZoomIn = () => setScale((s) => Math.min(s + 0.2, 5))
  const handleZoomOut = () => setScale((s) => Math.max(s - 0.2, 0.01))  // 最小 1%
  const handleZoomReset = () => {
    setScale(1)
    setPosition({ x: 0, y: 0 })
    // 同时重置背景图
    setBackgroundImage({ scale: 1, position: { x: 0, y: 0 } })
  }

  // 缩放控制（按钮）- 背景图
  const handleBgZoomIn = () => setBackgroundImage((prev) => ({
    ...prev,
    scale: Math.min(prev.scale + 0.1, 5)
  }))
  const handleBgZoomOut = () => setBackgroundImage((prev) => ({
    ...prev,
    scale: Math.max(prev.scale - 0.1, 0.01)  // 最小 1%
  }))
  const handleBgZoomReset = () => {
    setBackgroundImage({ scale: 1, position: { x: 0, y: 0 } })
  }

  // 智能拖拽处理（支持画布和背景图）
  const handleCanvasMouseDown = (e: React.MouseEvent) => {
    // 检查是否点击了交互元素（按钮、链接、可拖拽项等）
    const target = e.target as HTMLElement
    const isInteractive = target.closest('button') ||
                          target.closest('.ant-btn') ||
                          target.closest('a') ||
                          target.closest('[role="button"]') ||
                          target.closest('.ant-select') ||
                          target.closest('.ant-input')

    // 如果点击的是交互元素，不触发画布拖拽
    if (isInteractive) {
      return
    }

    // 检查是否正在拖拽设备（检查目标是否是可拖拽元素）
    const isDraggableDevice = target.closest('[draggable="true"]')
    if (isDraggableDevice) {
      return
    }

    // 其他情况（包括点击空白区域），允许拖拽
    const isAltKey = e.altKey  // Alt 键：移动背景图

    if (isAltKey) {
      // Alt + 拖拽：移动背景图
      setIsAdjustingBackground(true)
      // 记录初始状态：鼠标视口坐标和背景图位置（画布坐标系）
      setBackgroundDragStart({
        mouseX: e.clientX,
        mouseY: e.clientY,
        bgX: backgroundImage.position.x,
        bgY: backgroundImage.position.y,
      })
    } else {
      // 普通 + 拖拽：移动画布
      setIsDragging(true)
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y })
    }
  }

  const handleCanvasMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      // 移动画布
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      })
    } else if (isAdjustingBackground) {
      // 移动背景图（考虑画布的缩放比例）
      // 视口坐标增量需要转换为画布坐标增量
      const deltaX = (e.clientX - backgroundDragStart.mouseX) / scale
      const deltaY = (e.clientY - backgroundDragStart.mouseY) / scale

      setBackgroundImage({
        ...backgroundImage,
        position: {
          x: backgroundDragStart.bgX + deltaX,
          y: backgroundDragStart.bgY + deltaY,
        }
      })
    }
  }

  const handleCanvasMouseUp = () => {
    setIsDragging(false)
    setIsAdjustingBackground(false)
  }

  // 使用 useMemo 缓存 gridDimensions，避免每次渲染创建新对象
  const gridDimensions = React.useMemo(() => getGridDimensions(floorplan), [floorplan])

  // 计算比例尺数据（调用外部函数）
  const scaleBarData = calculateScaleBarData(scale, GRID_SIZE, CELL_PIXEL_SIZE)

  // 分离房间内未定位和已定位的设备
  const roomUndeployedDevices = roomDevices.filter((d) => {
    const pos = d.physical_position as any
    return !pos || !pos.floorplan || typeof pos.floorplan.grid_x !== 'number'
  })

  // 计算可见区域（使用 useMemo 避免闭包问题）
  const visibleRange = React.useMemo(() => {
    const containerWidth = 1000
    const containerHeight = 600

    const startGridX = Math.floor((-position.x) / (CELL_PIXEL_SIZE * scale))
    const endGridX = Math.ceil((containerWidth - position.x) / (CELL_PIXEL_SIZE * scale))
    const startGridY = Math.floor((-position.y) / (CELL_PIXEL_SIZE * scale))
    const endGridY = Math.ceil((containerHeight - position.y) / (CELL_PIXEL_SIZE * scale))

    return {
      startGridX: Math.max(0, startGridX),
      endGridX: Math.min(gridDimensions.width, endGridX + 1),
      startGridY: Math.max(0, startGridY),
      endGridY: Math.min(gridDimensions.height, endGridY + 1),
    }
  }, [position, scale, gridDimensions])

  // 过滤出可见区域内的设备，并根据层级筛选
  const roomDeployedDevices = roomDevices.filter((d) => {
    const pos = d.physical_position as any
    if (!pos || !pos.floorplan || typeof pos.floorplan.grid_x !== 'number') return false

    // 检查是否在可见区域内（添加缓冲区域以提高体验）
    const buffer = 2  // 缓冲 2 格
    const inVisibleRange = pos.floorplan.grid_x >= visibleRange.startGridX - buffer &&
           pos.floorplan.grid_x <= visibleRange.endGridX + buffer &&
           pos.floorplan.grid_y >= visibleRange.startGridY - buffer &&
           pos.floorplan.grid_y <= visibleRange.endGridY + buffer

    if (!inVisibleRange) return false

    // 层级过滤：如果设备设置了层级，只显示在选中的层级中
    const deviceLayer = getDeviceLayer(d)
    if (deviceLayer) {
      return selectedLayers.has(deviceLayer)
    }

    // 未设置层级的设备，显示在所有层级
    return true
  })

  // 上传平面图
  const handleUploadFloorplan = async (file: File) => {
    if (!selectedRoomId) {
      message.warning('请先选择房间')
      return false
    }

    setUploadingFloorplan(true)
    try {
      // 大型场馆配置：10000×10000 格 = 1000m×1000m
      await devicesApi.uploadFloorplan(selectedRoomId, file, 10000, 10000)
      message.success('平面图上传成功')
      // 清空初始 floorplan 缓存，准备加载新平面图
      initialFloorplanRef.current = null
      // 加载新平面图并恢复配置
      await loadRoomWithBackgroundConfig(selectedRoomId)
      // 延迟适配视口
      setTimeout(() => fitToViewport(), 100)
    } catch (error) {
      message.error('平面图上传失败')
      console.error(error)
    } finally {
      setUploadingFloorplan(false)
    }
    return false
  }

  // 打开添加设备模态框时设置默认值
  const handleOpenAddModal = () => {
    form.resetFields()
    // 设置默认设备类型为 light
    form.setFieldsValue({
      device_type: 'light',
    })
    setEditingDevice(null)
    setAddDeviceModalVisible(true)
  }

  // 添加/编辑设备
  const handleAddDevice = async () => {
    if (!selectedVenueId) {
      message.warning('请先选择场馆')
      return
    }

    try {
      const values = await form.validateFields()

      // 确保 device_category 有值（如果没有则根据 device_type 自动设置）
      const deviceCategory = values.device_category || DEVICE_TYPE_TO_CATEGORY[values.device_type]

      const createData: CreateDeviceRequest = {
        device_id: values.device_id,
        device_name: values.device_name,
        device_category: deviceCategory,
        device_type: values.device_type,
        venue_id: selectedVenueId,  // 添加到场馆
        // 不设置 room_id，后端会设为 NULL（可分配）
        location_description: values.location_description,
      }

      if (editingDevice) {
        // 更新设备 - 不需要 room_id
        await devicesApi.updateDevice(editingDevice.id, createData)
        message.success('设备更新成功')
      } else {
        // 创建设备
        await devicesApi.createDevice(selectedVenueId, createData)
        message.success('设备添加成功')
      }

      setAddDeviceModalVisible(false)
      setEditingDevice(null)
      form.resetFields()
      // 刷新可分配设备列表
      fetchVenueAvailableDevices(selectedVenueId)
      // 如果当前选中的是房间，也刷新房间设备列表
      if (selectedRoomId) {
        fetchRoomDevices(selectedRoomId)
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || (editingDevice ? '更新设备失败' : '添加设备失败'))
      console.error(error)
    }
  }

  // 删除设备
  const handleDeleteDevice = async (device: Device) => {
    try {
      await devicesApi.deleteDevice(device.id)
      message.success('设备删除成功')
      fetchRoomDevices(selectedRoomId!)
    } catch (error) {
      message.error('删除设备失败')
      console.error(error)
    }
  }

  // 编辑设备
  const handleEditDevice = (device: Device) => {
    setEditingDevice(device)
    form.setFieldsValue({
      device_id: device.device_id,
      device_name: device.device_name,
      device_type: device.device_type,
      location_description: device.location_description,
    })
    setAddDeviceModalVisible(true)
  }

  // 处理设备放置到网格（用于 GridCanvas 组件的 HTML5 拖放 API）
  const handleDeviceDrop = async (device: Device, gridX: number, gridY: number) => {
    if (!selectedRoomId) return

    // 检查位置是否被占用
    const existingDevice = roomDevices.find(
      (d) => {
        const pos = d.physical_position as any
        return (
          d.id !== device.id &&
          pos &&
          pos.floorplan &&
          pos.floorplan.grid_x === gridX &&
          pos.floorplan.grid_y === gridY
        )
      }
    )

    if (existingDevice) {
      message.warning(`位置 (${gridX}, ${gridY}) 已被占用`)
      return
    }

    // 判断是否从可分配设备拖来
    const isFromAvailable = !device.room_id

    // 保存设备位置
    setSavingLayout(true)
    try {
      const layoutData: FloorplanPosition = {
        grid_x: gridX,
        grid_y: gridY,
      }

      // 如果是可分配设备，先分配到房间
      if (isFromAvailable) {
        await devicesApi.updateDevice(device.id, { room_id: selectedRoomId })
        message.success(`设备已分配到房间`)
        fetchVenueAvailableDevices(selectedVenueId!)
        fetchRoomDevices(selectedRoomId)
      }

      // 保存位置信息
      await devicesApi.updateDeviceLayout(selectedRoomId, device.id, {
        is_deployed: true,
        floorplan: layoutData,
      })
      message.success(`设备已部署到 (${gridX}, ${gridY})`)
      fetchRoomDevices(selectedRoomId)
    } catch (error: any) {
      message.error(error.response?.data?.message || '保存设备位置失败')
      console.error(error)
    } finally {
      setSavingLayout(false)
    }
  }

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* 顶部: 标题 + 商家/场馆选择 */}
      <div style={{ padding: '16px 24px', borderBottom: '1px solid #f0f0f0' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col>
            <Space>
              <Title level={4} style={{ margin: 0 }}>设备部署管理</Title>
              {/* 视图切换按钮 */}
              {selectedVenueId && (
                <Button.Group>
                  <Button
                    type={viewMode === 'floorplan' ? 'primary' : 'default'}
                    icon={<AppstoreOutlined />}
                    onClick={() => setViewMode('floorplan')}
                  >
                    平面图
                  </Button>
                  <Button
                    type={viewMode === 'table' ? 'primary' : 'default'}
                    icon={<TableOutlined />}
                    onClick={() => setViewMode('table')}
                  >
                    设备列表
                  </Button>
                </Button.Group>
              )}
            </Space>
          </Col>
          <Col flex="auto">
            <Space size="large">
              <Select
                style={{ width: 200 }}
                placeholder="选择商家"
                value={selectedMerchantId}
                onChange={setSelectedMerchantId}
                loading={merchantsLoading}
              >
                {merchants.map((m) => (
                  <Option key={m.id} value={m.id}>{m.company_name}</Option>
                ))}
              </Select>
              <Select
                style={{ width: 200 }}
                placeholder="选择场馆"
                value={selectedVenueId}
                onChange={setSelectedVenueId}
                disabled={!selectedMerchantId}
                loading={venuesLoading}
              >
                {venues.map((v) => (
                  <Option key={v.id} value={v.id}>{v.venue_name}</Option>
                ))}
              </Select>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleOpenAddModal}
                disabled={!selectedVenueId}
              >
                添加设备
              </Button>
            </Space>
          </Col>
          <Col>
            <Space>
              {/* 批量操作模式切换 */}
              {selectedVenueId && (
                <Button
                  type={isBatchMode ? 'primary' : 'default'}
                  icon={<CheckSquareOutlined />}
                  onClick={() => {
                    setIsBatchMode(!isBatchMode)
                    setSelectedDeviceIds(new Set())
                  }}
                >
                  {isBatchMode ? '退出批量' : '批量操作'}
                </Button>
              )}
              <Button
                icon={<ReloadOutlined />}
                onClick={() => {
                  if (selectedVenueId) {
                    fetchVenueAvailableDevices(selectedVenueId)
                  }
                  if (selectedRoomId) {
                    fetchRoomDevices(selectedRoomId)
                  }
                }}
                loading={loading}
                disabled={!selectedVenueId}
              >
                刷新
              </Button>
            </Space>
          </Col>
        </Row>

        {/* 批量操作工具栏 */}
        {isBatchMode && selectedDeviceIds.size > 0 && (
          <Row style={{ marginTop: 12, padding: '12px 24px', background: '#e6f7ff', borderRadius: 8 }}>
            <Col flex="auto">
              <Space>
                <Text strong>已选择 {selectedDeviceIds.size} 个设备</Text>
                <Divider type="vertical" />
                <Button
                  size="small"
                  danger
                  icon={<MinusOutlined />}
                  onClick={handleBatchRemove}
                >
                  移除到未分配
                </Button>
                <Button
                  size="small"
                  type="primary"
                  icon={<CarryOutOutlined />}
                  onClick={() => {
                    const targetRoom = rooms.find(r => r.id !== selectedRoomId)
                    if (targetRoom) {
                      handleBatchAddToRoom(targetRoom.id)
                    } else {
                      message.warning('没有其他房间可添加')
                    }
                  }}
                  disabled={rooms.length <= 1}
                >
                  添加到其他房间
                </Button>
                <Button
                  size="small"
                  onClick={() => setSelectedDeviceIds(new Set())}
                >
                  取消选择
                </Button>
              </Space>
            </Col>
          </Row>
        )}
      </div>

      {/* 主体: 左右两栏布局 */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* 左侧: 房间列表 + 可分配设备列表 + 操作说明 */}
          <div style={{ width: 280, borderRight: '1px solid #f0f0f0', overflowY: 'auto', background: '#fafafa' }}>
            <div style={{ padding: 16 }}>
              {/* 房间列表 */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <Text strong>房间列表</Text>
              </div>
              <Spin spinning={roomsLoading}>
                <List
                  dataSource={rooms}
                  renderItem={(room) => (
                    <List.Item
                      key={room.id}
                      style={{
                        padding: '12px',
                        background: selectedRoomId === room.id ? '#e6f7ff' : 'transparent',
                        borderRadius: 4,
                        cursor: 'pointer',
                        marginBottom: 4,
                        border: selectedRoomId === room.id ? '1px solid #1890ff' : '1px solid transparent',
                      }}
                      onClick={() => setSelectedRoomId(room.id)}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <Space>
                          <HomeOutlined />
                          <Text strong={selectedRoomId === room.id}>{room.room_name}</Text>
                        </Space>
                        <Badge count={getRoomDeviceCount(room.id)} showZero />
                      </div>
                    </List.Item>
                  )}
                />
              </Spin>

              {/* 可分配设备列表（场馆级别） */}
              <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #e8e8e8' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <Text strong style={{ fontSize: 12, color: '#666' }}>可分配设备</Text>
                  <Badge count={availableDevices.length} showZero />
                </div>
                {availableDevices.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 20, color: '#999', fontSize: 12 }}>
                    暂无可分配设备
                  </div>
                ) : (
                  <>
                    {isBatchMode && (
                      <Button size="small" onClick={() => toggleSelectAll(availableDevices)} style={{ marginBottom: 8 }}>
                        {selectedDeviceIds.size === availableDevices.length ? '取消全选' : '全选'}
                      </Button>
                    )}
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      {availableDevices.map((device) => (
                        <DraggableDevice
                          key={device.id}
                          device={device}
                          compact
                          selectable={isBatchMode}
                          isSelected={selectedDeviceIds.has(device.id)}
                          toggleDeviceSelection={toggleDeviceSelection}
                          handleRemoveDeviceFromRoom={handleRemoveDeviceFromRoom}
                        />
                      ))}
                    </Space>
                  </>
                )}
              </div>

              {/* 操作说明 */}
              <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #e8e8e8' }}>
                <Text strong style={{ fontSize: 12, color: '#666' }}>操作说明</Text>
                <div style={{ marginTop: 8, fontSize: 12, color: '#999', lineHeight: 1.8 }}>
                  <div style={{ marginBottom: 4 }}>📌 基本操作：</div>
                  <div style={{ marginLeft: 12 }}>• 拖拽空白区域：移动画布</div>
                  <div style={{ marginLeft: 12 }}>• 滚轮：缩放画布</div>
                  <div style={{ marginLeft: 12 }}>• Shift + 滚轮：快速缩放</div>
                  <div style={{ marginBottom: 4, marginTop: 4 }}>🎨 背景图调整：</div>
                  <div style={{ marginLeft: 12 }}>• Alt + 拖拽：移动背景图</div>
                  <div style={{ marginLeft: 12 }}>• Alt + 滚轮：缩放背景图</div>
                  <div style={{ marginBottom: 4, marginTop: 4 }}>📍 设备部署：</div>
                  <div style={{ marginLeft: 12 }}>• 拖拽左侧设备到网格</div>
                  <div style={{ marginLeft: 12 }}>• 点击设备查看详情</div>
                </div>
              </div>
            </div>
          </div>

          {/* 中间: 平面图区域 OR 设备配置表格 */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
            <Spin spinning={loading || savingLayout || devicesLoading} tip={savingLayout ? '保存中...' : loading ? '加载中...' : devicesLoading ? '加载中...' : '加载中...'}>
              {viewMode === 'table' && selectedVenueId ? (
                /* 设备列表视图 */
                <Card
                  title={
                    <Space>
                      <span>设备配置列表</span>
                      <Text type="secondary">共 {allDevices.length} 个设备</Text>
                    </Space>
                  }
                  extra={
                    <Space>
                      {/* 过滤器 */}
                      <Select
                        style={{ width: 120 }}
                        placeholder="房间"
                        allowClear
                        value={tableFilters.roomId}
                        onChange={(value) => setTableFilters({ ...tableFilters, roomId: value })}
                      >
                        {rooms.map((r) => (
                          <Option key={r.id} value={r.id}>{r.room_name}</Option>
                        ))}
                      </Select>
                      <Select
                        style={{ width: 120 }}
                        placeholder="设备类型"
                        allowClear
                        value={tableFilters.deviceType}
                        onChange={(value) => setTableFilters({ ...tableFilters, deviceType: value || undefined })}
                      >
                        <Option value="light">灯光</Option>
                        <Option value="speaker">音响</Option>
                        <Option value="screen">屏幕</Option>
                        <Option value="sensor">传感器</Option>
                        <Option value="camera">摄像头</Option>
                      </Select>
                      <Select
                        style={{ width: 100 }}
                        placeholder="状态"
                        allowClear
                        value={tableFilters.status}
                        onChange={(value) => setTableFilters({ ...tableFilters, status: value || undefined })}
                      >
                        <Option value="online">在线</Option>
                        <Option value="offline">离线</Option>
                      </Select>
                      <Divider type="vertical" />
                      <Button icon={<ReloadOutlined />} onClick={fetchAllDevices}>刷新</Button>
                    </Space>
                  }
                >
                  <Table
                    dataSource={allDevices}
                    rowKey="id"
                    pagination={{
                      ...tablePagination,
                      showSizeChanger: true,
                      showTotal: (total) => `共 ${total} 个设备`,
                      onChange: (page, pageSize) => setTablePagination({ current: page, pageSize }),
                    }}
                    scroll={{ y: 500 }}
                    columns={[
                      {
                        title: 'ID',
                        dataIndex: 'id',
                        width: 60,
                        fixed: 'left' as const,
                      },
                      {
                        title: '设备ID',
                        dataIndex: 'device_id',
                        width: 120,
                        ellipsis: true,
                      },
                      {
                        title: '设备名称',
                        dataIndex: 'device_name',
                        width: 180,
                        ellipsis: true,
                        render: (text, record) => (
                          editingCoordinate?.id === record.id && editingCoordinate.field === 'device_name' ? (
                            <Space.Compact style={{ width: '100%' }}>
                              <Input
                                defaultValue={text}
                                size="small"
                                autoFocus
                                onBlur={(e) => handleDeviceInfoEdit(record.id, 'device_name', e.target.value)}
                                onPressEnter={(e) => handleDeviceInfoEdit(record.id, 'device_name', e.currentTarget.value)}
                                style={{ flex: 1 }}
                              />
                              <Button
                                type="primary"
                                size="small"
                                icon={<SaveOutlined />}
                                onClick={() => {
                                  const input = document.querySelector('input[value]') as HTMLInputElement
                                  if (input) {
                                    handleDeviceInfoEdit(record.id, 'device_name', input.value)
                                  }
                                }}
                              />
                              <Button
                                size="small"
                                icon={<CloseOutlined />}
                                onClick={() => setEditingCoordinate(null)}
                              />
                            </Space.Compact>
                          ) : (
                            <a onClick={() => setEditingCoordinate({ id: record.id, field: 'device_name', value: text })}>
                              {text}
                            </a>
                          )
                        ),
                      },
                      {
                        title: '类型',
                        dataIndex: 'device_type',
                        width: 80,
                        render: (type) => (
                          <Space>
                            <span>{DEVICE_ICONS[type] || DEVICE_ICONS.default}</span>
                            <span style={{ fontSize: 12 }}>{type}</span>
                          </Space>
                        ),
                      },
                      {
                        title: '状态',
                        dataIndex: 'status',
                        width: 80,
                        render: (status) => (
                          <Badge
                            status={status === 'online' ? 'success' : 'default'}
                            text={status === 'online' ? '在线' : '离线'}
                          />
                        ),
                      },
                      {
                        title: '房间',
                        dataIndex: 'room_id',
                        width: 120,
                        render: (roomId) => {
                          const room = rooms.find((r) => r.id === roomId)
                          return room ? (
                            <Tag color="blue">{room.room_name}</Tag>
                          ) : (
                            <Tag>未分配</Tag>
                          )
                        },
                      },
                      {
                        title: '网格坐标',
                        width: 180,
                        render: (_, record) => {
                          const pos = (record.physical_position as any)?.floorplan
                          const gridX = pos?.grid_x
                          const gridY = pos?.grid_y

                          if (typeof gridX !== 'number' || typeof gridY !== 'number') {
                            return <Text type="secondary">未定位</Text>
                          }

                          const isEditingX = editingCoordinate?.id === record.id && editingCoordinate.field === 'gridX'
                          const isEditingY = editingCoordinate?.id === record.id && editingCoordinate.field === 'gridY'

                          return (
                            <Space size="small">
                              <span>X:</span>
                              {isEditingX ? (
                                <InputNumber
                                  defaultValue={gridX}
                                  size="small"
                                  min={0}
                                  max={10000}
                                  style={{ width: 80 }}
                                  autoFocus
                                  onBlur={(e) => handleCoordinateEdit(record.id, 'gridX', parseFloat(e.target.value) || 0)}
                                  onPressEnter={(e) => handleCoordinateEdit(record.id, 'gridX', parseFloat(e.currentTarget.value) || 0)}
                                />
                              ) : (
                                <a onClick={() => setEditingCoordinate({ id: record.id, field: 'gridX', value: gridX })}>
                                  {gridX}
                                </a>
                              )}
                              <span>Y:</span>
                              {isEditingY ? (
                                <InputNumber
                                  defaultValue={gridY}
                                  size="small"
                                  min={0}
                                  max={10000}
                                  style={{ width: 80 }}
                                  autoFocus
                                  onBlur={(e) => handleCoordinateEdit(record.id, 'gridY', parseFloat(e.target.value) || 0)}
                                  onPressEnter={(e) => handleCoordinateEdit(record.id, 'gridY', parseFloat(e.currentTarget.value) || 0)}
                                />
                              ) : (
                                <a onClick={() => setEditingCoordinate({ id: record.id, field: 'gridY', value: gridY })}>
                                  {gridY}
                                </a>
                              )}
                            </Space>
                          )
                        },
                      },
                      {
                        title: '操作',
                        key: 'action',
                        width: 120,
                        fixed: 'right' as const,
                        render: (_, record) => (
                          <Space size="small">
                            {record.room_id && (
                              <Popconfirm
                                title="确认移除到未分配？"
                                onConfirm={() => handleRemoveDeviceFromRoom(record.id)}
                                okText="确认"
                                cancelText="取消"
                              >
                                <Button type="link" size="small" danger icon={<MinusOutlined />}>
                                  移除
                                </Button>
                              </Popconfirm>
                            )}
                            <Button
                              type="link"
                              size="small"
                              icon={<EditOutlined />}
                              onClick={() => setEditingDevice(record)}
                            >
                              编辑
                            </Button>
                          </Space>
                        ),
                      },
                    ]}
                  />
                </Card>
              ) : viewMode === 'floorplan' && selectedRoomId ? (
                <>
                  {/* 平面图区域 */}
                  <div style={{ padding: 24, paddingBottom: 0 }}>
                    <Card
                    title={`当前房间: ${rooms.find((r) => r.id === selectedRoomId)?.room_name || ''}`}
                    extra={
                      <Space size="middle">
                        {/* 层级选择器 */}
                        <Space size="small" split={<Divider type="vertical" />}>
                          <Text type="secondary" style={{ fontSize: 12 }}>显示层级:</Text>
                          <Checkbox.Group
                            value={Array.from(selectedLayers)}
                            onChange={(values) => {
                              setSelectedLayers(new Set(values as DeviceLayer[]))
                            }}
                            options={Object.entries(LAYER_CONFIG).map(([key, config]) => ({
                              label: (
                                <Tooltip title={config.description}>
                                  <span style={{ color: config.color }}>
                                    {config.icon} {config.name}
                                  </span>
                                </Tooltip>
                              ),
                              value: key,
                            }))}
                          />
                        </Space>
                        <Divider type="vertical" />
                        <Button size="small" onClick={fitToViewport}>
                          适配视口
                        </Button>
                        <Upload
                          accept="image/*"
                          showUploadList={false}
                          beforeUpload={handleUploadFloorplan}
                        >
                          <Button icon={<UploadOutlined />} loading={uploadingFloorplan}>
                            上传平面图
                          </Button>
                        </Upload>
                      </Space>
                    }
                  >
                    {/* 缩放和平移容器 */}
                    <div
                      ref={canvasContainerRef}
                      data-canvas-background="true"
                      style={{
                        position: 'relative',
                        width: '100%',
                        height: 600,
                        overflow: 'hidden',
                        border: '1px solid #d9d9d9',
                        borderRadius: 4,
                        cursor: isDragging ? 'grabbing' : 'grab',
                      }}
                      onMouseDown={handleCanvasMouseDown}
                      onMouseMove={handleCanvasMouseMove}
                      onMouseUp={handleCanvasMouseUp}
                      onMouseLeave={handleCanvasMouseUp}
                    >
                      {/* 可缩放和平移的画布 */}
                      <GridCanvas
                        scale={scale}
                        position={position}
                        gridDimensions={gridDimensions}
                        floorplan={floorplan}
                        roomUndeployedDevices={roomUndeployedDevices}
                        roomDeployedDevices={roomDeployedDevices}
                        availableDevices={availableDevices}
                        onDeviceDrop={handleDeviceDrop}
                        isDragging={isDragging}
                        CELL_PIXEL_SIZE={CELL_PIXEL_SIZE}
                        backgroundImage={backgroundImage}
                        isBatchMode={isBatchMode}
                        selectedDeviceIds={selectedDeviceIds}
                        toggleDeviceSelection={toggleDeviceSelection}
                        toggleSelectAll={toggleSelectAll}
                        handleRemoveDeviceFromRoom={handleRemoveDeviceFromRoom}
                      >
                        {roomDeployedDevices.map((device) => {
                          const pos = (device.physical_position as any)?.floorplan
                          if (!pos || typeof pos.grid_x !== 'number') return null
                          return (
                            <DraggableGridDevice
                              key={device.id}
                              device={device}
                              x={pos.grid_x}
                              y={pos.grid_y}
                              cellSize={CELL_PIXEL_SIZE}
                              scale={scale}
                              savingLayout={savingLayout}
                            />
                          )
                        })}
                      </GridCanvas>

                      {/* 画布缩放控制 - 右侧 */}
                      <ZoomSlider
                        value={scale}
                        onChange={(val) => setScale(val)}
                        label="画布"
                        style={{
                          position: 'absolute',
                          right: 16,
                          top: '50%',
                          transform: 'translateY(-50%)',
                        }}
                      />

                      {/* 背景图缩放控制 - 左侧（水平） */}
                      {floorplan?.image_url && (
                        <ZoomSlider
                          value={backgroundImage.scale}
                          onChange={(val) => {
                            setBackgroundImage(prev => ({ ...prev, scale: val }))
                          }}
                          label="背景图"
                          vertical={false}
                          style={{
                            position: 'absolute',
                            left: 16,
                            bottom: 16,
                          }}
                        />
                      )}

                      {/* 比例尺 - 右下角 */}
                      <ScaleBar
                        position="bottom-right"
                        pixelLength={scaleBarData.pixelLength}
                        label={scaleBarData.label}
                        scalePercent={scaleBarData.scalePercent}
                      />

                      {/* 提示文字 */}
                      {!floorplan?.image_url && (
                        <div style={{ marginTop: 16, textAlign: 'center', color: '#999' }}>
                          <Text type="secondary">拖拽设备到网格中进行部署，或上传平面图作为背景</Text>
                        </div>
                      )}
                    </div>
                  </Card>
                  </div>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>
                  <HomeOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                  <div>请从左侧选择房间</div>
                </div>
              )}
            </Spin>
          </div>
      </div>

      {/* 添加/编辑设备模态框 */}
      <Modal
        title={editingDevice ? '编辑设备' : '添加设备'}
        open={addDeviceModalVisible}
        onOk={handleAddDevice}
        onCancel={() => {
          setAddDeviceModalVisible(false)
          setEditingDevice(null)
          form.resetFields()
        }}
        destroyOnClose
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="设备 ID"
            name="device_id"
            rules={[{ required: true, message: '请输入设备 ID' }]}
          >
            <Input placeholder="例如: light_ceiling_001" disabled={!!editingDevice} />
          </Form.Item>

          <Form.Item
            label="设备名称"
            name="device_name"
            rules={[{ required: true, message: '请输入设备名称' }]}
          >
            <Input placeholder="例如: 天花板主灯" />
          </Form.Item>

          <Form.Item
            label="设备类型"
            name="device_type"
            rules={[{ required: true, message: '请选择设备类型' }]}
          >
            <Select
              options={DEVICE_TYPE_OPTIONS}
              placeholder="选择设备类型"
            />
          </Form.Item>

          <Form.Item label="位置描述" name="location_description">
            <TextArea rows={3} placeholder="例如: 房间中央天花板位置" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default DeviceDeployment
